import { useState, useEffect } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import { useAuth } from '../lib/auth';
import { auditAPI, billingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Shield, ArrowLeft, Download, FileText, AlertTriangle,
  Search, Link2, EyeOff, BarChart3, Lock, Trash2
} from 'lucide-react';

const issueIcons = {
  hardcoded_value: Search,
  formula_inconsistency: AlertTriangle,
  circular_reference: BarChart3,
  external_reference: Link2,
  hidden_sheet: EyeOff,
  hidden_row: EyeOff,
  hidden_column: EyeOff,
  protected_sheet: Shield,
};

// Static ghost rows displayed blurred beneath the 5 real errors for Free users (desc keys for i18n)
const GHOST_ROWS = [
  { sev: 'critical', sheet: 'Sheet1',      cell: 'B14', descKey: 'teaser.ghost_1' },
  { sev: 'critical', sheet: 'Données',     cell: 'C22', descKey: 'teaser.ghost_2' },
  { sev: 'warning',  sheet: 'Budget',      cell: 'D8',  descKey: 'teaser.ghost_3' },
  { sev: 'critical', sheet: 'Summary',     cell: 'E31', descKey: 'teaser.ghost_4' },
  { sev: 'warning',  sheet: 'P&L',         cell: 'F5',  descKey: 'teaser.ghost_5' },
  { sev: 'critical', sheet: 'Hypothèses',  cell: 'G17', descKey: 'teaser.ghost_6' },
  { sev: 'warning',  sheet: 'Cash Flow',   cell: 'H9',  descKey: 'teaser.ghost_7' },
  { sev: 'critical', sheet: 'Bilan',       cell: 'I26', descKey: 'teaser.ghost_8' },
  { sev: 'warning',  sheet: 'Ratios',      cell: 'J3',  descKey: 'teaser.ghost_9' },
];

export default function Report() {
  const { auditId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { user } = useAuth();

  // Always fetch from API for the authoritative, normalized response.
  // location.state is only a fallback if the network call fails.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await auditAPI.detail(auditId);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled && location.state?.auditData) {
          const fallback = location.state.auditData;
          const aiReport = (fallback.ai_report_en && fallback.ai_report_fr)
            ? (fallback[`ai_report_${language}`] || fallback.ai_report)
            : fallback.ai_report;
          setData({ ...fallback, ai_report: aiReport });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId, language]);

  const handleDownload = async () => {
    try {
      const res = await auditAPI.downloadPdf(auditId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SheetArmor_Audit_${data?.filename || auditId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const handleDownloadCsv = async () => {
    try {
      const res = await auditAPI.downloadCsv(auditId, language);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SheetArmor_Full_Audit_Trail_${auditId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const handleUpgrade = () => {
    // Redirect to pricing (like Dashboard) so user can choose monthly vs annual
    window.location.href = '/#pricing';
  };

  const handleKillSwitch = async () => {
    const confirmed = window.confirm(t('report.kill_switch_confirm'));
    if (!confirmed) return;
    try {
      await auditAPI.delete(auditId);
      navigate('/dashboard');
    } catch {
      alert(language === 'fr'
        ? 'Échec de la suppression. Veuillez réessayer.'
        : 'Failed to delete audit. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white">
        <p className="font-body text-neutral-500">{t('report.not_found')}</p>
      </div>
    );
  }

  // ─── Derived state ────────────────────────────────────────────────────────
  // Backend contract for BOTH routes (upload POST + detail GET):
  //   data.total_issues   → canonical total count — set at analysis time, never overwritten
  //   data.critical_count → canonical critical count — same guarantee
  //   data.warning_count  → canonical warning count — same guarantee
  //   data.results.issues → sliced to 5 for Free users, full for paid
  //   data.locked         → true for Free plan audits

  const isPaid   = user?.plan === 'auditor' || user?.plan === 'firm';
  const isLocked = data.locked === true;

  const results  = data.results || {};
  const aiReport = data.ai_report || {};
  const issues   = results.issues || [];

  const score = data.health_score ?? results.health_score ?? 0;

  // STRICT: Use backend canonical counters only — never issues.length or .filter()
  const totalIssuesCount = data.total_issues ?? 0;
  const totalCritical    = data.critical_count ?? 0;
  const totalWarning     = data.warning_count ?? 0;

  const scoreColor = score >= 80 ? 'text-green-500' : score >= 50 ? 'text-yellow-500' : 'text-red-500';
  const scoreBg    = score >= 80 ? 'bg-green-500/10 border-green-500/20' : score >= 50 ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-red-500/10 border-red-500/20';
  const riskLevel  = aiReport.risk_level || (score >= 80 ? 'LOW' : score >= 50 ? 'MEDIUM' : 'HIGH');

  const issueSummary   = data.issue_summary || {};
  const affectedSheets = data.affected_sheets || [];

  // Build breakdown by type from issue_summary (backend canonical) — not from sliced issues
  const issuesByType = {};
  Object.entries(issueSummary).forEach(([type, info]) => {
    issuesByType[type] = info?.count ?? 0;
  });
  if (Object.keys(issuesByType).length === 0) {
    issues.forEach(i => { issuesByType[i.type] = (issuesByType[i.type] || 0) + 1; });
  }

  // Teaser state — exactly 3 ghost rows
  const hiddenCount = Math.max(0, totalIssuesCount - 5);
  const ghostCount  = Math.min(3, hiddenCount);
  const showTeaser  = !isPaid && hiddenCount > 0;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="report-page">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#050505]/90 backdrop-blur-lg sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between min-h-[64px]">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-neutral-400 hover:text-white" data-testid="report-back-link">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
            <span className="font-heading text-lg">{t('report.title')}</span>
            {isLocked && (
              <Badge className="bg-yellow-600/20 text-yellow-400 text-xs">
                <Lock className="w-3 h-3 mr-1" strokeWidth={2} />{t('teaser.locked')}
              </Badge>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              {/* CSV: Firm plan only */}
              {!isLocked && (
                user?.plan === 'firm' ? (
                  <Button onClick={handleDownloadCsv}
                    variant="ghost"
                    className="border border-white/10 hover:bg-white/5 text-white text-xs font-body tracking-widest h-9 rounded-sm"
                    data-testid="download-csv-button">
                    <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {t('report.download_csv')}
                  </Button>
                ) : (
                  <Button onClick={() => handleUpgrade('firm')}
                    variant="ghost"
                    className="border border-yellow-600/30 hover:bg-yellow-600/10 text-yellow-400 text-xs font-body tracking-widest h-9 rounded-sm"
                    data-testid="unlock-csv-button">
                    <Lock className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {t('report.unlock_csv')}
                  </Button>
                )
              )}

              {/* PDF: available for ALL plans — Free gets a 5-error PDF */}
              <Button onClick={handleDownload}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-body tracking-widest h-9 rounded-sm"
                data-testid="download-pdf-button">
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {t('report.download_pdf')}
              </Button>

              {/* Kill-switch: Firm plan only */}
              {!isLocked && (
                user?.plan === 'firm' ? (
                  <Button onClick={handleKillSwitch}
                    variant="ghost"
                    className="border border-red-600/30 hover:bg-red-600/10 text-red-400 text-xs font-body tracking-widest h-9 rounded-sm"
                    data-testid="kill-switch-button">
                    <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {t('report.kill_switch')}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    disabled
                    className="border border-neutral-700 text-neutral-600 text-xs font-body tracking-widest h-9 rounded-sm cursor-not-allowed opacity-50"
                    data-testid="kill-switch-locked-button">
                    <Lock className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {t('report.kill_switch_locked')}
                  </Button>
                )
              )}
            </div>
            {user?.plan === 'free' && (
              <p className="text-neutral-500 text-[11px] font-body max-w-md text-right">
                {t('report.free_limit_notice')}
              </p>
            )}
            {user?.plan === 'auditor' && (
              <p className="text-neutral-500 text-[11px] font-body max-w-md text-right">
                {t('report.auditor_limit_notice')}
              </p>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 pt-6">
        {/* Score + Metadata cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10 rounded-sm overflow-hidden mb-8">
          <div className={`p-6 border ${scoreBg} flex flex-col items-center justify-center`}>
            <p className="text-neutral-500 text-xs font-body mb-1 uppercase tracking-wider">{t('report.score')}</p>
            <p className={`font-heading text-5xl ${scoreColor}`} data-testid="health-score">{score}</p>
            <p className="text-neutral-600 text-xs font-body mt-1">/100</p>
          </div>
          <div className="bg-[#0A0A0A] p-6 flex flex-col justify-center">
            <p className="text-neutral-500 text-xs font-body mb-1 uppercase tracking-wider">{t('report.risk')}</p>
            <Badge className={`w-fit ${
              riskLevel === 'HIGH' || riskLevel === 'ELEVE' ? 'bg-red-600/20 text-red-400' :
              riskLevel === 'MEDIUM' || riskLevel === 'MOYEN' ? 'bg-yellow-600/20 text-yellow-400' :
              'bg-green-600/20 text-green-400'
            } text-sm`} data-testid="risk-level">
              {riskLevel}
            </Badge>
          </div>
          <div className="bg-[#0A0A0A] p-6">
            <p className="text-neutral-500 text-xs font-body mb-2 uppercase tracking-wider">{t('report.file')}</p>
            <p className="font-body text-sm text-white truncate">{data.filename}</p>
            <p className="font-mono text-xs text-neutral-600 mt-1">
              {data.total_sheets ?? results.total_sheets} {t('report.sheets')}
              {' / '}{(data.total_cells ?? results.total_cells_analyzed)?.toLocaleString()} {t('report.cells')}
            </p>
          </div>
          {/* PROBLÈMES — bound strictly to canonical root-level counters */}
          <div className="bg-[#0A0A0A] p-6">
            <p className="text-neutral-500 text-xs font-body mb-2 uppercase tracking-wider">{t('report.issues')}</p>
            <p className="font-heading text-3xl text-white" data-testid="total-issues-count">{totalIssuesCount}</p>
            <p className="font-mono text-xs text-neutral-600 mt-1">
              {totalCritical} {t('report.critical_count')} / {totalWarning} {t('report.warnings_count')}
            </p>
          </div>
        </div>

        {/* Executive Summary */}
        {aiReport.executive_summary && (
          <div className="border border-white/10 bg-[#0A0A0A] rounded-sm p-6 mb-6" data-testid="executive-summary">
            <h3 className="font-heading text-lg text-white mb-3">{t('report.summary')}</h3>
            <p className="font-body text-sm text-neutral-300 leading-relaxed">{aiReport.executive_summary}</p>
          </div>
        )}

        {/* Issues Breakdown */}
        <div className="border border-white/10 bg-[#0A0A0A] rounded-sm p-6 mb-6" data-testid="issues-breakdown">
          <h3 className="font-heading text-lg text-white mb-4">{t('report.findings')}</h3>

          {Object.keys(issuesByType).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {Object.entries(issuesByType).map(([type, count]) => {
                const Icon = issueIcons[type] || AlertTriangle;
                return (
                  <div key={type} className="bg-white/[0.02] border border-white/5 rounded-sm p-3">
                    <Icon className="w-4 h-4 text-blue-600 mb-2" strokeWidth={1.5} />
                    <p className="font-mono text-lg text-white">{count}</p>
                    <p className="text-neutral-500 text-xs font-body">{t(`report.issue_type_${type}`) || type.replace(/_/g, ' ')}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Visible issues (backend already sliced to 5 for Free) */}
          {issues.length > 0 && (
            <div className="space-y-1">
              {issues.map((issue, i) => {
                const Icon = issueIcons[issue.type] || AlertTriangle;
                return (
                  <div key={i} className="flex items-start gap-3 p-2 hover:bg-white/[0.02] rounded-sm" data-testid={`issue-${i}`}>
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      issue.severity === 'critical' ? 'text-red-500' :
                      issue.severity === 'warning' ? 'text-yellow-500' : 'text-neutral-500'
                    }`} strokeWidth={1.5} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <Badge variant="outline" className="text-[10px] text-neutral-400 border-white/10">
                          {t('report.sheet')}: {issue.sheet}
                        </Badge>
                        {issue.cell && (
                          <span className="font-mono text-xs text-blue-400">{t('report.cell')}: {issue.cell}</span>
                        )}
                        <Badge className={`text-[10px] ${
                          issue.severity === 'critical' ? 'bg-red-600/20 text-red-400' :
                          issue.severity === 'warning' ? 'bg-yellow-600/20 text-yellow-400' :
                          'bg-neutral-600/20 text-neutral-400'
                        }`}>{t(`report.severity_${issue.severity}`) || issue.severity}</Badge>
                      </div>
                      <p className="font-body text-xs text-neutral-400">{issue.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Auditor plan upsell: >50 errors, link to pricing */}
          {user?.plan === 'auditor' && totalIssuesCount > 50 && (
            <div className="text-center py-4 text-slate-400 text-sm font-body">
              {t('report.auditor_upsell_prefix')}
              <Link to="/#pricing" className="text-blue-500 hover:underline">{t('report.auditor_upsell_link')}</Link>
              {t('report.auditor_upsell_suffix').replace('{count}', totalIssuesCount)}
            </div>
          )}

          {/* ── Blur Teaser + Paywall overlay ─────────────────────────────────
              Shown for Free users when there are hidden issues (hiddenCount > 0).
              Ghost rows are rendered below the 5 real errors, blurred and faded.
              The paywall box floats over them with a negative margin.            */}
          {showTeaser && (
            <div className="relative mt-2" data-testid="freemium-blur-section">
              {/* Ghost rows — blurred/faded, non-interactive */}
              <div
                className="space-y-1 blur-md opacity-40 select-none pointer-events-none"
                aria-hidden="true"
              >
                {GHOST_ROWS.slice(0, ghostCount).map((row, i) => {
                  const Icon = i % 3 === 0 ? AlertTriangle : i % 3 === 1 ? Search : BarChart3;
                  return (
                    <div key={i} className="flex items-start gap-3 p-2 rounded-sm">
                      <Icon
                        className={`w-4 h-4 mt-0.5 flex-shrink-0 ${row.sev === 'critical' ? 'text-red-500' : 'text-yellow-500'}`}
                        strokeWidth={1.5}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <Badge variant="outline" className="text-[10px] text-neutral-400 border-white/10">
                            {t('report.sheet')}: {row.sheet}
                          </Badge>
                          <span className="font-mono text-xs text-blue-400">{t('report.cell')}: {row.cell}</span>
                          <Badge className={`text-[10px] ${row.sev === 'critical' ? 'bg-red-600/20 text-red-400' : 'bg-yellow-600/20 text-yellow-400'}`}>
                            {row.sev === 'critical' ? t('report.severity_critical') : t('report.severity_warning')}
                          </Badge>
                        </div>
                        <p className="font-body text-xs text-neutral-400">{t(row.descKey)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Paywall box — overlays the blurred rows with -mt-20 z-10 */}
              <div
                className="-mt-20 relative z-10 border-2 border-blue-600/40 bg-[#050505]/95 backdrop-blur-sm rounded-sm p-6"
                data-testid="freemium-paywall"
              >
                <div className="flex items-start gap-4">
                  <Lock className="w-8 h-8 text-blue-500 flex-shrink-0 mt-1" strokeWidth={1.5} />
                  <div className="flex-1">
                    <h4 className="font-heading text-xl text-white mb-2">{t('teaser.unlock')}</h4>
                    <p className="text-neutral-400 text-sm font-body mb-1">
                      {t('teaser.more_errors').replace('{count}', hiddenCount)}
                    </p>
                    <p className="text-neutral-500 text-xs font-body mb-4">
                      {t('teaser.subscribe')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button onClick={() => handleUpgrade('auditor')}
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/5 h-10 px-6 text-xs font-body tracking-widest rounded-sm"
                        data-testid="freemium-paywall-auditor-btn">
                        {t('dash.auditor')} — {language === 'fr' ? '99€' : '$99'}/mo
                      </Button>
                      <Button onClick={() => handleUpgrade('firm')}
                        className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-6 text-xs font-body tracking-widest rounded-sm"
                        data-testid="freemium-paywall-firm-btn">
                        {t('dash.firm')} — {language === 'fr' ? '249€' : '$249'}/mo
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Critical Alerts */}
        {aiReport.critical_alerts?.length > 0 && (
          <div className="border border-red-600/20 bg-red-600/5 rounded-sm p-6 mb-6" data-testid="critical-alerts">
            <h3 className="font-heading text-lg text-white mb-4">{t('report.alerts')}</h3>
            <div className="space-y-4">
              {aiReport.critical_alerts.map((alert, i) => (
                <div key={i} className="border-l-2 border-red-600 pl-4">
                  <p className="font-body text-sm text-white font-semibold mb-1">{alert.title}</p>
                  <p className="font-body text-xs text-neutral-400 mb-1">{alert.description}</p>
                  {alert.recommendation && (
                    <p className="font-body text-xs text-blue-400">{t('report.rec')}: {alert.recommendation}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {!isLocked && aiReport.recommendations?.length > 0 && (
          <div className="border border-white/10 bg-[#0A0A0A] rounded-sm p-6" data-testid="recommendations">
            <h3 className="font-heading text-lg text-white mb-4">{t('report.recommendations')}</h3>
            <div className="space-y-3">
              {aiReport.recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Badge className={`text-xs flex-shrink-0 mt-0.5 ${
                    rec.priority === 'HIGH' || rec.priority === 'HAUTE' ? 'bg-red-600/20 text-red-400' :
                    rec.priority === 'MEDIUM' || rec.priority === 'MOYENNE' ? 'bg-yellow-600/20 text-yellow-400' :
                    'bg-green-600/20 text-green-400'
                  }`}>{rec.priority === 'HIGH' || rec.priority === 'HAUTE' ? t('report.priority_high') : rec.priority === 'MEDIUM' || rec.priority === 'MOYENNE' ? t('report.priority_medium') : rec.priority === 'LOW' || rec.priority === 'FAIBLE' ? t('report.priority_low') : rec.priority}</Badge>
                  <div>
                    <p className="font-body text-sm text-white">{rec.action}</p>
                    {rec.rationale && <p className="font-body text-xs text-neutral-500 mt-0.5">{rec.rationale}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full paywall for locked audits with 0 or ≤5 visible issues (no teaser) */}
        {isLocked && !showTeaser && (
          <div className="border-2 border-blue-600/40 bg-blue-600/5 rounded-sm p-8 text-center" data-testid="paywall">
            <Lock className="w-10 h-10 text-blue-500 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-heading text-2xl text-white mb-2">{t('teaser.unlock')}</h3>
            <p className="text-neutral-400 text-sm font-body mb-6 max-w-md mx-auto">{t('teaser.subscribe')}</p>

            {affectedSheets.length > 0 && (
              <div className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-sm">
                <span className="text-neutral-500 text-xs font-body">{t('teaser.affected_sheets')}:</span>
                <span className="text-white text-xs font-mono">{affectedSheets.join(', ')}</span>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => handleUpgrade('auditor')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5 h-12 px-8 text-xs font-body tracking-widest rounded-sm"
                data-testid="paywall-auditor-btn">
                {t('dash.auditor')} — {language === 'fr' ? '99\u20ac' : '$99'}/mo
              </Button>
              <Button onClick={() => handleUpgrade('firm')}
                className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-8 text-xs font-body tracking-widest rounded-sm"
                data-testid="paywall-firm-btn">
                {t('dash.firm')} — {language === 'fr' ? '249\u20ac' : '$249'}/mo
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
